'use client';
import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '../Icon';
import ProgramCard from '@/components/vster/ProgramCard';
import { c } from '@/lib/vster/theme';
import type { Program } from '@/lib/vster/types';
import type { PublicProgram } from '@/lib/vster/programs';

type ProgramItem = Program & { id?: number; domainLabel?: string | null };
interface HomeStats { newCount: number; openCount: number; closingSoon: number; agencyCount: number; siteCount: number; bizinfoOpenCount: number; collectedAt: string }
interface Props { navigate?: (s: string) => void; programs?: ProgramItem[]; stats?: HomeStats }

// 많이 찾는 조건 — 클릭 시 목록으로 이동(검색어/지역).
const QUICK_CONDS: { label: string; q: Record<string, string> }[] = [
  { label: '인력·고용', q: { q: '인력·고용' } },
  { label: 'R&D·기술개발', q: { q: 'R&D' } },
  { label: '수출·판로', q: { q: '수출' } },
  { label: '서울', q: { region: '서울' } },
];
// 수집 출처(신뢰 스트립)
const SOURCE_CHIPS = ['기업마당', 'K-Startup', '중소벤처24', 'SBA', 'KOTRA', 'TIPA'];

const VALUE_PROPS = [
  { icon: 'layers', color: c.action, title: '기업마당보다 더 넓게', desc: '정부·지자체·공공기관의 다양한 지원사업을 매일 찾아드립니다.' },
  { icon: 'ads_click', color: '#12A46B', title: '우리 회사 맞춤 추천', desc: '지역·업종·기업정보를 기반으로 신청 가능성이 높은 사업을 추천합니다' },
  { icon: 'notifications_active', color: c.action, title: '놓치지 않는 맞춤 알림', desc: '신규 공고와 마감임박 사업을 카카오톡/이메일로 알려드립니다' },
] as const;

const HOW_STEPS = [
  { n: '1', head: '찾고', title: '분야·지역으로 공고를 좁히세요', sub: '600개 기관 공고가 한 목록에 모여 있습니다' },
  { n: '2', head: '확인하고', title: '되는 공고만 골라 3줄로 확인', sub: '자가진단 + AI요약으로 자격·핵심 한눈에' },
  { n: '3', head: '챙긴다', title: '마감·신규를 이메일로 챙겨드려요', sub: '관심 저장하면 놓칠 일 없이 리마인드' },
] as const;


export default function HomeScreen({ programs, stats }: Props) {
  const router = useRouter();
  // 신규 지원사업 최신 TOP10 — 기본순(최신순) 상위 10건.
  const featured = useMemo(() => (programs ?? []).slice(0, 10), [programs]);

  const goPrograms = (q?: Record<string, string>) => {
    const u = new URLSearchParams();
    if (q) for (const [k, v] of Object.entries(q)) if (v) u.set(k, v);
    router.push(('/programs' + (u.toString() ? `?${u}` : '')) as never);
  };

  // 히어로 '기업마당 vs 부스터' 비교 카드 수치.
  const bizinfoOpen = stats?.bizinfoOpenCount ?? 0;
  const boosterOpen = stats?.openCount ?? 0;
  const openDiff = Math.max(0, boosterOpen - bizinfoOpen);
  const bizinfoBarPct = boosterOpen ? Math.max(8, Math.min(100, Math.round((bizinfoOpen / boosterOpen) * 100))) : 38;
  return (
    <main style={{ background: c.bg, color: c.ink, animation: 'vfade .35s ease' }}>
      <style>{`
        .bs-hero{display:grid;grid-template-columns:minmax(0,1.15fr) minmax(300px,.85fr);gap:48px;align-items:center}
        @media(max-width:900px){.bs-hero{grid-template-columns:minmax(0,1fr);gap:32px}}
        .bs-a:hover{color:${c.actionDark}}
      `}</style>

      {/* ── Hero · 지원사업, 찾지 마세요 (기업마당 vs 부스터 비교) ── */}
      <section style={{ borderBottom: `1px solid ${c.line}`, background: 'linear-gradient(180deg,#FBFCFE 0%,#fff 42%)' }}>
        <div className="bs-hero" style={{ maxWidth: 1180, margin: '0 auto', padding: '72px 24px 56px' }}>
          {/* 좌: 카피 + CTA */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, width: 'max-content', color: c.actionDark, fontSize: 12, fontWeight: 700, letterSpacing: '.12em' }}>
              <span style={{ width: 22, height: 1, background: c.action }} />정부지원사업·행사 통합검색 · 맞춤알림
            </span>
            <h1 className="vh1" style={{ fontSize: 48, lineHeight: 1.14, letterSpacing: '-.04em', fontWeight: 800, margin: '18px 0 16px', color: c.ink }}>
              지원사업, 찾지 마세요.<br />내게 맞는 공고를<br /><span style={{ color: c.action }}>부스터</span>가 알려드립니다.
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: c.muted, margin: '0 0 30px', maxWidth: 490 }}>
              관심 분야와 조건만 설정하세요. 기업마당을 넘어 더 많은 지원사업과 행사·교육 공고를 찾아 <strong style={{ color: c.ink, fontWeight: 700 }}>우리 회사에 맞는 공고만 골라 알려드립니다.</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <button onClick={() => router.push('/signup' as never)} style={{ height: 54, background: c.action, color: '#fff', border: 'none', borderRadius: 12, padding: '0 30px', fontFamily: 'inherit', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px -4px rgba(44,92,255,.5)' }}>맞춤공고 알림 설정하기</button>
                <button onClick={() => goPrograms()} style={{ background: 'none', border: 'none', fontFamily: 'inherit', fontSize: 15, fontWeight: 600, color: c.muted, cursor: 'pointer', padding: '6px 2px' }}>지원사업 직접 검색하기 →</button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: c.muted }}>많이 찾는 조건</span>
                {QUICK_CONDS.map((cd) => (
                  <button key={cd.label} onClick={() => goPrograms(cd.q)} style={{ fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, padding: '6px 12px', border: `1px solid ${c.line}`, borderRadius: 999, background: c.surface, color: c.muted, cursor: 'pointer' }}>{cd.label}</button>
                ))}
              </div>
            </div>
          </div>

          {/* 우: 기업마당 vs 부스터 비교 카드 */}
          <div style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 16, padding: 24, boxShadow: '0 12px 32px -12px rgba(14,23,38,.14)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${c.line}` }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: c.ink }}>지원사업 비교</span>
              <span className="num" style={{ fontSize: 12, fontWeight: 600, color: c.muted }}>{(stats?.collectedAt ?? '').replace(' 07:00 기준', ' 기준')}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 44px minmax(0,1.5fr)', alignItems: 'stretch' }}>
              <div style={{ background: '#FBFCFE', border: `1px solid ${c.line}`, borderRadius: 13, padding: '18px 14px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: c.muted, marginBottom: 10 }}>기업마당</div>
                <div className="num" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.035em', lineHeight: 1, color: c.muted }}>{bizinfoOpen.toLocaleString()}<span style={{ fontSize: 13, fontWeight: 600, marginLeft: 3 }}>건</span></div>
                <div style={{ height: 8, borderRadius: 999, background: c.surfaceAlt, overflow: 'hidden', marginTop: 'auto' }}>
                  <div style={{ width: `${bizinfoBarPct}%`, height: '100%', borderRadius: 999, background: '#C5CEDC' }} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ width: 34, height: 34, borderRadius: '50%', background: c.surface, border: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: c.muted }}>VS</span>
              </div>
              <div style={{ background: '#F7F9FF', border: `1.5px solid ${c.action}`, borderRadius: 13, padding: '18px 16px', textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 10 }}>
                  <Icon name="bolt" size={16} color={c.action} />
                  <span style={{ fontSize: 13.5, fontWeight: 800, color: c.actionDark }}>부스터</span>
                </div>
                <div className="num" style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-.04em', lineHeight: 1, color: c.actionDark }}>{boosterOpen.toLocaleString()}<span style={{ fontSize: 15, fontWeight: 600, marginLeft: 4 }}>건</span></div>
                <div style={{ height: 8, borderRadius: 999, background: c.surfaceAlt, overflow: 'hidden', marginTop: 14 }}>
                  <div style={{ width: '100%', height: '100%', borderRadius: 999, background: c.action }} />
                </div>
              </div>
            </div>
            <div style={{ margin: '16px 0', background: c.actionSoft, borderRadius: 13, padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ flexShrink: 0, width: 44, height: 44, borderRadius: '50%', background: c.action, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="arrow_upward" size={24} color="#fff" />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="num" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.035em', lineHeight: 1.1, color: c.actionDark }}>+{openDiff.toLocaleString()}건의 사업기회를 더 발견</div>
                <p style={{ fontSize: 13, lineHeight: 1.55, color: c.muted, margin: '7px 0 0' }}>기업마당에서 놓칠 수 있는 사업기회까지 찾아드립니다.</p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, paddingTop: 16, borderTop: `1px solid ${c.line}` }}>
              {[
                { icon: 'library_books', v: boosterOpen.toLocaleString(), k: '전체 공고', color: c.action, href: '/programs' as string | undefined },
                { icon: 'fiber_new', v: (stats?.newCount ?? 0).toLocaleString(), k: '신규(7일)', color: c.action, href: '/programs' as string | undefined },
                { icon: 'schedule', v: (stats?.closingSoon ?? 0).toLocaleString(), k: '마감임박', color: c.danger, href: '/programs' as string | undefined },
                { icon: 'lan', v: (stats?.siteCount ?? 0).toLocaleString(), k: '수집사이트', color: c.ink, href: '/sites' as string | undefined },
              ].map((s) => (
                <div key={s.k} onClick={s.href ? () => router.push(s.href as never) : undefined} style={{ textAlign: 'center', cursor: s.href ? 'pointer' : 'default' }}>
                  <Icon name={s.icon} size={20} color={c.action} />
                  <div className="num" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.025em', lineHeight: 1, color: s.color, marginTop: 7 }}>{s.v}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: c.muted, marginTop: 5 }}>{s.k}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, color: c.muted, marginTop: 14 }}>
              매일 업데이트 · 중복 공고 제외 · 동일 기간 기준
            </div>
          </div>
        </div>
      </section>

      {/* ── 이번 주 지원사업 TOP10 배너 → /weekly ── */}
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 24px 0' }}>
        <a href="/weekly" style={{ display: 'flex', alignItems: 'center', gap: 14, background: `linear-gradient(135deg, ${c.ink} 0%, #16204a 48%, ${c.action} 135%)`, color: '#fff', borderRadius: 14, padding: '16px 22px', textDecoration: 'none', boxShadow: '0 10px 30px -18px rgba(0,61,155,.5)', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,.16)', border: '1px solid rgba(255,255,255,.26)', borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 700, letterSpacing: '.03em', whiteSpace: 'nowrap' }}>🔥 이번 주 TOP10</span>
          <span style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '-.01em' }}>지금 가장 주목받는 접수중 지원사업 10선 — 한눈에 보기</span>
          <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>바로가기 →</span>
        </a>
      </section>

      {/* ── 신뢰 스트립 ── */}
      <section style={{ background: c.surface, borderBottom: `1px solid ${c.line}`, padding: '18px 24px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: c.ink }}>
            <Icon name="sync" size={20} color={c.action} />매일 아침, 정부·지자체·기관 공고를 자동 수집합니다
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginLeft: 'auto' }}>
            {SOURCE_CHIPS.map((s) => (
              <span key={s} style={{ fontSize: 12, fontWeight: 600, color: c.muted, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 6, padding: '4px 9px' }}>{s}</span>
            ))}
            <span style={{ fontSize: 12, color: c.muted }}>· 매일 갱신</span>
          </span>
        </div>
      </section>

      {/* ── 핵심 가치 ── */}
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '48px 24px 32px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 12 }}>
          {VALUE_PROPS.map((v) => (
            <div key={v.title} style={{
              background: c.surface, border: `1px solid ${c.line}`,
              borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <Icon name={v.icon} size={26} color={v.color} />
              <h3 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.015em', margin: '2px 0 0', color: c.ink }}>{v.title}</h3>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: c.muted, margin: 0 }}>{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 신규 지원사업 최신 TOP10 ── */}
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '16px 24px 48px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
          <div>
            <h2 className="vh2" style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-.035em', margin: '0 0 6px', color: c.ink }}>신규 지원사업 TOP10</h2>
            <p style={{ margin: 0, fontSize: 14, color: c.muted }}>가장 최근 등록된 지원사업 10건</p>
          </div>
          <button onClick={() => goPrograms()} className="bs-a" style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: c.action, fontSize: 15, fontWeight: 600 }}>
            전체 공고 보기 <Icon name="arrow_forward" size={19} color={c.action} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,440px),1fr))', gap: 14, alignItems: 'start' }}>
          {featured.map((p, i) => (
            <div key={p.id ?? i} style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', top: 10, left: 10, zIndex: 2,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 8,
                background: i < 3 ? c.action : c.ink, color: '#fff',
                fontSize: 13, fontWeight: 800,
              }}>{i + 1}</span>
              <ProgramCard p={p as unknown as PublicProgram} />
            </div>
          ))}
        </div>
      </section>

      {/* ── 작동 방식 ── */}
      <section style={{ background: c.surface, borderTop: `1px solid ${c.line}`, borderBottom: `1px solid ${c.line}`, padding: '48px 24px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <h2 className="vh2" style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-.025em', margin: '0 0 8px', color: c.ink }}>찾고 → 확인하고 → 챙긴다</h2>
          <p style={{ fontSize: 15, color: c.muted, margin: '0 0 24px' }}>가입하면 이 흐름이 매주 자동으로 돌아갑니다</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 12 }}>
            {HOW_STEPS.map((s) => (
              <div key={s.n} style={{ background: c.bg, border: `1px solid ${c.line}`, borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 6, background: c.ink, color: '#fff', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.n}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.04em', color: c.muted }}>{s.head}</span>
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-.015em', margin: 0, color: c.ink }}>{s.title}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: c.muted, margin: 0 }}>{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 채용지원금 계산기(곧 출시) — 최종 CTA 바로 위 ── */}
      <section className="vpad" style={{ padding: '32px 24px 0' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <div className="vcol-sm" style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, padding: '28px 32px', display: 'flex', alignItems: 'center', gap: 24 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'inline-block', background: c.surfaceAlt, color: c.muted, fontSize: 12.5, fontWeight: 700, borderRadius: 999, padding: '4px 11px', marginBottom: 12 }}>곧 출시</span>
              <h2 className="vh3" style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-.025em', color: c.ink, margin: '0 0 8px' }}>직원 한 명 채용하면, 지원금은 얼마일까요?</h2>
              <p style={{ fontSize: 15, color: c.muted, lineHeight: 1.6, margin: 0 }}>채용 조건을 입력하면 <strong style={{ color: c.ink, fontWeight: 700 }}>우리 회사가 받을 수 있는 채용지원금과 예상 금액</strong>을 바로 확인할 수 있습니다.</p>
            </div>
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <button onClick={() => router.push('/jobs' as never)} style={{ background: c.action, color: '#fff', border: 'none', cursor: 'pointer', padding: '14px 24px', borderRadius: 10, fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>예상 지원금 확인하기</button>
              <span style={{ fontSize: 12, color: c.muted }}>예상 금액이며 실제 지급액은 지원요건에 따라 달라질 수 있습니다.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── 최종 CTA ── */}
      <section style={{ padding: '48px 24px 64px' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', background: c.ink, borderRadius: 14, padding: '48px 32px', textAlign: 'center', color: '#fff' }}>
          <h2 className="vh2" style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-.025em', margin: '0 0 10px' }}>받을 수 있는 지원금부터, 지금 확인하세요</h2>
          <p style={{ fontSize: 15, color: '#C8D0DE', margin: '0 0 24px' }}>카드 없이 30초 가입</p>
          <button onClick={() => router.push('/signup' as never)} style={{
            display: 'inline-block', background: c.action, color: '#fff', border: 'none', cursor: 'pointer',
            padding: '14px 32px', borderRadius: 10, fontSize: 16, fontWeight: 700,
          }}>무료로 시작하기</button>
        </div>
      </section>
    </main>
  );
}
